import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Gear, Lightbulb, BookOpenText, X } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Dialog, DialogContent } from "@libs/zapi-ui/shadcn";
import { FigmaTypography, AppButton, AppTextarea } from "@libs/zapi-ui/custom";
import { chapterIcons, EChapterIcon, defaultChapterIcon } from "@libs/zapi-ui/const";

export type TInstructionsModalChapter = {
  slug: string;
  name: string;
  icon?: string | null;
};

interface InstructionsModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  chapters: TInstructionsModalChapter[];
  projectInstructions: string;
  chaptersInstructions: Record<string, string>;
  presentationPrompt: string;
  onProjectInstructionsChange: (projectInstructions: string, chaptersInstructions: Record<string, string>) => void;
  onPresentationPromptChange: (value: string) => void;
  defaultSection?: string;
}

export function InstructionsModal({
  open,
  onClose,
  onSave,
  isSaving,
  chapters,
  projectInstructions,
  chaptersInstructions,
  presentationPrompt,
  onProjectInstructionsChange,
  onPresentationPromptChange,
  defaultSection = "project-instructions"
}: InstructionsModalProps) {
  const { t } = useTranslation("projectHome");
  const [selectedSection, setSelectedSection] = useState<string>(defaultSection);

  const isProjectInstructions = selectedSection === "project-instructions";
  const isOutlineInstructions = selectedSection === "outline-instructions";
  const currentChapter = chapters.find((ch) => ch.slug === selectedSection);

  function getChapterIcon(iconName?: string | null) {
    if (!iconName) return chapterIcons[defaultChapterIcon];
    return chapterIcons[iconName as EChapterIcon] ?? chapterIcons[defaultChapterIcon];
  }

  function getSectionTitle() {
    if (isProjectInstructions) return t("instructionsModal.projectInstructions");
    if (isOutlineInstructions) return t("instructionsModal.outlineInstructions");
    return currentChapter?.name || t("instructionsModal.pageInstructions");
  }

  function getSectionPlaceholder() {
    if (isProjectInstructions) return t("instructionsModal.projectPlaceholder");
    if (isOutlineInstructions) return t("instructionsModal.outlinePlaceholder");
    return t("instructionsModal.pagePlaceholder", { name: currentChapter?.name || t("instructionsModal.thisPage") });
  }

  function getSectionValue() {
    if (isProjectInstructions) return projectInstructions;
    if (isOutlineInstructions) return presentationPrompt;
    return chaptersInstructions[selectedSection] || "";
  }

  function handleChange(value: string) {
    if (isProjectInstructions) {
      onProjectInstructionsChange(value, chaptersInstructions);
    } else if (isOutlineInstructions) {
      onPresentationPromptChange(value);
    } else {
      onProjectInstructionsChange(projectInstructions, { ...chaptersInstructions, [selectedSection]: value });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[1200px] h-[750px] p-0 gap-0 overflow-hidden [&>button]:hidden">
        <div className="flex h-full min-h-0">
          <div className="w-[320px] min-w-[320px] bg-innoveraZinc-50 border-r border-innoveraZinc-150 flex flex-col rounded-l-lg min-h-0">
            <div className="p-5 border-b border-innoveraZinc-150 shrink-0">
              <div className="flex items-center gap-2">
                <Gear className="w-5 h-5" />
                <FigmaTypography variant="heading-3">{t("instructionsModal.title")}</FigmaTypography>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5 min-h-0">
              <div className="flex flex-col gap-1">
                <FigmaTypography variant="label-3" color="zinc500" className="px-2 py-2">
                  {t("instructionsModal.general")}
                </FigmaTypography>
                <button
                  onClick={() => setSelectedSection("project-instructions")}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors",
                    "border border-transparent",
                    isProjectInstructions ? "bg-white border-innoveraZinc-150" : "hover:bg-innoveraZinc-100"
                  )}
                >
                  <Lightbulb size={16} className="flex-shrink-0" />
                  <FigmaTypography variant="label-2" color="zinc900">
                    {t("instructionsModal.projectInstructions")}
                  </FigmaTypography>
                </button>
              </div>

              <div className="flex flex-col gap-1">
                <FigmaTypography variant="label-3" color="zinc500" className="px-2 py-2">
                  {t("instructionsModal.presentation")}
                </FigmaTypography>
                <button
                  onClick={() => setSelectedSection("outline-instructions")}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors",
                    "border border-transparent",
                    isOutlineInstructions ? "bg-white border-innoveraZinc-150" : "hover:bg-innoveraZinc-100"
                  )}
                >
                  <BookOpenText size={16} className="flex-shrink-0" />
                  <FigmaTypography variant="label-2" color="zinc900">
                    {t("instructionsModal.outlineInstructions")}
                  </FigmaTypography>
                </button>
              </div>

              {chapters.length > 0 && (
                <div className="flex flex-col gap-1">
                  <FigmaTypography variant="label-3" color="zinc500" className="px-2 py-2">
                    {t("instructionsModal.pages")}
                  </FigmaTypography>
                  <div className="flex flex-col gap-1">
                    {chapters.map((chapter) => {
                      const isSelected = selectedSection === chapter.slug;
                      const ChapterIconComponent = getChapterIcon(chapter.icon);
                      return (
                        <button
                          key={chapter.slug}
                          onClick={() => setSelectedSection(chapter.slug)}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors",
                            "border border-transparent",
                            isSelected ? "bg-white border-innoveraZinc-150" : "hover:bg-innoveraZinc-100"
                          )}
                        >
                          <ChapterIconComponent size={16} className="flex-shrink-0" />
                          <FigmaTypography variant="label-2" color="zinc900">
                            {chapter.name}
                          </FigmaTypography>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            <div className="flex items-center justify-between p-5 border-b border-innoveraZinc-150 shrink-0">
              <FigmaTypography variant="heading-3">{getSectionTitle()}</FigmaTypography>
              <button onClick={onClose} className="p-1">
                <X size={20} className="text-innoveraZinc-500" />
              </button>
            </div>

            <div className="flex-1 p-5 min-h-0">
              <AppTextarea
                value={getSectionValue()}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={getSectionPlaceholder()}
                className="!h-full w-full resize-none"
                containerClassName="h-full"
                fullWidth
              />
            </div>

            <div className="flex items-center justify-end p-5 border-t border-innoveraZinc-150 shrink-0">
              <AppButton
                appearance="contained"
                color="zinc950"
                onClick={onSave ?? onClose}
                isLoading={isSaving}
                loadingText={t("instructionsModal.saving")}
              >
                {t("instructionsModal.saveChanges")}
              </AppButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
